import SubTitle from 'components/SubTitle';
import HorizontalScrollIndicators from 'components/uiLibrary/HorizontalScrollIndicators';
import LinkButton from 'components/uiLibrary/LinkButton';
import VisitWebsiteLink from 'components/VisitWebsiteLink';
import { TP } from 'constants/index';
import AgencyOfficesTable from 'containers/Managers/AgencyOfficesTable';
import AgencyRoster from 'containers/Managers/AgencyRoster';
import queries from 'containers/Managers/queries';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'src/i18n';
import { makeStyles } from 'src/makeStyles';
import { useManagerAgreements, useManagerConnections } from 'utils/hooks/useManagerQueries';
import useScrollToActiveElement from 'utils/hooks/useScrollToActiveElement';
import { useQuery } from 'utils/react-query';

const useStyles = makeStyles()(theme => ({
  navigation: {
    marginBottom: theme.spacing(3),
    position: 'sticky',
    top: 80, // Desktop header height
    zIndex: 10,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      top: 60, // Tablet header height
    },
    [theme.breakpoints.down('sm')]: {
      top: 120, // Mobile header height + sticky header height
    },
  },
  navigation__tab: {
    margin: '6px 0 0 0',
    minWidth: 'fit-content',
    '& + &': {
      marginLeft: '8px',
    },
  },
  scrollLeftIndicator: {
    background: 'linear-gradient(90deg, #FFF -21.86%, #FFF 68.51%)',
    boxShadow: '-2px 0px 10px rgba(0, 0, 0, 0.3)',
    borderRight: 'none',
    paddingLeft: 8,
  },
  scrollRightIndicator: {
    background: 'linear-gradient(90deg, #FFF -21.86%, #FFF 68.51%)',
    boxShadow: '-2px 0px 10px rgba(0, 0, 0, 0.3)',
    borderLeft: 'none',
    paddingRight: '8px',
  },
  section: {
    marginTop: theme.spacing(4),
  },
  agencyOfficeInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    margin: 0,
  },
  agencyWebLink: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const getUniqueGroupNames = groupedConnections =>
  Object.keys(groupedConnections)
    .filter(key => key !== 'unacknowledged' && key !== 'organizations')
    .map(key => {
      const group = groupedConnections[key];
      return group?.[0]?.groupName;
    })
    .filter(Boolean);

const AgencyRosterView = ({ entity }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const elementRefs = useRef({});

  const { data: connections } = useManagerConnections();
  const { data: agreements } = useManagerAgreements();

  // Filter to only include acknowledged connections for public display
  const acknowledgedConnections = connections?.filter(connection => connection?.acknowledged);

  // Fetch office data
  const { data: offices } = useQuery(
    queries.getManagerOffices({
      id: entity?.id,
      queryConfig: { select: response => response?.data, enabled: !!entity?.id },
    }),
  );

  const groupedConnections = useMemo(() => {
    if (!acknowledgedConnections || !agreements) {
      return {};
    }

    const defaultConnectionGroup = {
      unacknowledged: [],
    };

    return (
      acknowledgedConnections?.reduce((acc, connection) => {
        const { agencyConnectionProfessions, organization } = connection;

        if (organization?.id) {
          if (!acc.organizations) {
            acc.organizations = [];
          }
          acc.organizations.push({
            ...connection,
            agreements: agreements?.[connection?.id]?.default || [],
          });
        } else if (agencyConnectionProfessions) {
          agencyConnectionProfessions.forEach(connectionProfession => {
            const { id: connectionProfessionId, profession } = connectionProfession || {};
            const professionId = profession?.id;
            const connectionAgreements = agreements?.[connection?.id]?.[connectionProfessionId] || [];

            const connectionDuplicate = {
              ...connection,
              groupName: profession?.name,
              connectionProfession,
              agreements: connectionAgreements,
            };

            if (!acc[professionId]) {
              acc[professionId] = [];
            }

            acc[professionId].push(connectionDuplicate);
          });
        }

        return acc;
      }, defaultConnectionGroup) || defaultConnectionGroup
    );
  }, [acknowledgedConnections, agreements]);

  // Get unique profession group names for navigation
  const allGroupNames = useMemo(() => getUniqueGroupNames(groupedConnections), [groupedConnections]);

  // Scroll functionality for profession navigation
  const { activeName, scrollToElement } = useScrollToActiveElement({ elementRefs });

  // Check if this is an agency organization (type 70)
  if (!entity?.organizationType?.id || entity.organizationType.id !== 70) {
    return null;
  }

  return (
    <>
      {/* View office & team button - positioned above profession navigation */}
      {offices?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <LinkButton variant="text" onClick={() => scrollToElement('Office')}>
            {t(`${TP}.VIEW_OFFICE_TEAM_BTN`)}
          </LinkButton>
        </div>
      )}

      {/* Navigation with Profession tabs */}
      {allGroupNames.length > 0 && (
        <div className={classes.navigation}>
          <HorizontalScrollIndicators
            triggerSize={0}
            styles={{ leftIndicator: classes.scrollLeftIndicator, rightIndicator: classes.scrollRightIndicator }}
          >
            {allGroupNames.map((name, index) => (
              <LinkButton
                styles={{
                  root: classes.navigation__tab,
                }}
                variant="grey"
                shape="rectangle"
                active={activeName === name}
                key={`mlink_${index}`}
                onClick={() => scrollToElement(name)}
              >
                {name}
              </LinkButton>
            ))}
          </HorizontalScrollIndicators>
        </div>
      )}

      {/* Agency Roster Content - View mode (no edit buttons) */}
      <AgencyRoster
        connections={groupedConnections}
        editMode={false}
        agencyData={entity}
        elementRefs={elementRefs}
        isEditPanelVisible={false}
        addToRosterButton={null}
      />

      {/* Office & Team Section */}
      {offices?.length > 0 && (
        <section className={classes.section}>
          <div className={classes.agencyOfficeInfo}>
            <SubTitle className={classes.sectionTitle}>
              <span
                ref={el => {
                  if (el) {
                    elementRefs.current.Office = el;
                  }
                }}
                data-name="Office"
              >
                {t(`${TP}.VIEW_OFFICE_TEAM_BTN`)}
              </span>
            </SubTitle>
            {entity?.url && (
              <div className={classes.agencyWebLink}>
                <VisitWebsiteLink href={entity?.url} />
              </div>
            )}
          </div>
          <AgencyOfficesTable offices={offices} />
        </section>
      )}
    </>
  );
};

export default AgencyRosterView; 